import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import { Settlement } from '~hooks/useSettlements/models';

interface VoidSettlementFormData {
  reason: string;
}

interface VoidSettlementFormProps {
  settlement?: Settlement;
  isSubmitting: boolean;
  onSubmit: (data: VoidSettlementFormData) => Promise<void>;
  mode: 'void' | 'void-rebill';
}

const voidSettlementSchema = yup.object({
  reason: yup
    .string()
    .trim()
    .max(100, t('form_validation_errors.max_length', { max: 100 }))
    .notRequired(),
});

export default function VoidSettlementForm({
  settlement,
  isSubmitting,
  onSubmit,
  mode,
}: VoidSettlementFormProps) {
  const { control, handleSubmit, formState } = useForm<VoidSettlementFormData>({
    resolver: yupResolver(voidSettlementSchema),
    defaultValues: {
      reason: '',
    },
  });

  const getWarningText = () => {
    if (mode === 'void-rebill') {
      return t('approvals.driver_pay.settlements.void_rebill_settlement.warning', {
        settlementId: settlement?.settlementId || '',
      });
    }
    return t('approvals.driver_pay.settlements.void_settlement.warning', {
      settlementId: settlement?.settlementId || '',
    });
  };

  const totalAmount = settlement?.originalSettlement
    ? settlement.rebillTotalAmount
    : settlement?.totalAmount;

  return (
    <form id="void-settlement-form" onSubmit={handleSubmit(onSubmit)}>
      <Alert variant="outlined" severity="info" color="warning" sx={{ mb: 2 }}>
        {getWarningText()}
      </Alert>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {t('approvals.driver_pay.settlements.void_settlement.settlement_id')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {settlement?.settlementId}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {t('approvals.driver_pay.settlements.void_settlement.for')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {settlement?.customerAccount?.name || ''}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {t('approvals.driver_pay.settlements.void_settlement.total')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {`$${(totalAmount ?? 0).toFixed(2)}`}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {t('approvals.driver_pay.settlements.void_settlement.reason_label')}
      </Typography>
      <TextFormField
        control={control}
        errors={formState.errors}
        name="reason"
        disabled={isSubmitting}
        multiline={true}
        rows={3}
        sx={{ mb: 1 }}
      />
    </form>
  );
}
